<template>
  <div>
    <LevelsLoading v-if="loading" />
    <div v-else id="external-content" v-html="externalContent" class="external-content"></div>
    
    <!-- Enable this code to print the Axios error message on the page -->
    <!-- <p v-if="errorMessage">
      <error404 />
      {{ errorMessage }}
    </p> -->
  </div>
</template>

<script lang="ts">
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';
import { axiosClassic } from '@/services/axiosConfig';
import { showToast } from '@/services/notifications/toastService';
import { defineComponent, ref, watch, onMounted, onUpdated } from 'vue';
import { ReturnJSCode, ReturnJSDefs } from '@/services/dynamicJavaScript';
import { getResourceUrl, sportTitle } from '@/services/admin_actions/apiUtils';
import { startTimer, stopTimer } from '@/services/admin_actions/pageTimerService';

import router from '@/router';
import Error404 from '@/views/AdminPages/Error404.vue';
import LevelsLoading from './ComponentLibrary/LevelsLoading.vue';

const overlay = ref(true); // Controls the visibility of the overlay
declare let $: any;

export default defineComponent({
  setup() {
    // Creating reactive variables
    const loading = ref(true); // A flag to indicate loading state
    const overlay = ref(true); // Controls the visibility of the overlay
    const route = useRoute(); // Accessing the current route
    const externalContent = ref(''); // To store the fetched content
    const errorMessage = ref(''); // To store any error messages
    let JsCodeFromAxiosResponse = ref(''); // To store the URL of the dynamic script
    let JsDefs = ref(''); // To store the URL of the dynamic script
    let LoadExternalScripts = ref(false); // To if we want to load the external scripts we only want to do it once per axois request.
    let formsReadyForListeners = ref(false); // To add listeners to form elements for intercepting submit events
    let IsRedirect = ref(false); // To check if the response is a redirect
    let LoadScript = ref('');
    let LoadJsDefs = ref('');
    
    // Function to strip the language prefix from the path
    function stripLangPrefix(path: string): string {
      const parts = path.split('/')
      if (['en', 'fr'].includes(parts[1])) {
        parts.splice(1, 1)
        return parts.join('/') || '/'
      }
      return path
    }
    
    // Function to fetch content from a given path
    const fetchContent = async (path: string) => {
      loading.value = true;
      try {
        // Performing the GET request
        const response = await axiosClassic.get(`${path}`, {
          withCredentials: true,
        });
        if (response.status === 200) {
          // Handling successful response
          await InsertResponseIntoDOM(response);
          
          // Get X-Guzzle-Redirect-History header and set router to that URL
          // this is the last url that classic site redirects to
          var redirectUrl = response.headers['x-guzzle-redirect-history'];
          if (redirectUrl) {
            UpdatePathWithUrl(redirectUrl);
          }
        } else {
          // Handling unsuccessful response
          errorMessage.value = `Failed to fetch content: ${response.statusText}`;
          // Trigger toast for error
          showToast('error', 'We are having trouble connecting, please refresh or try again later.');
        }
      } catch (error) {
        // Catching and handling errors
        errorMessage.value = `Error fetching the site: ${error}`;
        showToast('error', 'We are having trouble connecting, please refresh or try again later.');
      } finally {
        // Resetting the loading state
        loading.value = false;
        if (!loading.value) stopTimer('bodyContent');
        SendEventWhenContentLoaded();
      }
    };
    // Function to add listeners to form elements for intercepting submit events
    const addListenerToForms = () => {
      document.querySelectorAll('.classic_form').forEach((form) => {
        form.addEventListener('submit', (e) => {
          FormHandler(form as HTMLFormElement, e);
        });
        form.addEventListener('overrideformsubmit', (e) => {
          FormHandler(form as HTMLFormElement, e);
        });
        formsReadyForListeners.value = true;
      });
      // Get all inputs with type submit and add event listener
      document.querySelectorAll('input[type="submit"]').forEach((input) => {
        input.addEventListener('click', (e) => {
          let trElement = input.closest('tr');
          let form = trElement?.querySelector('form');
          if (form) {
            FormHandler(form, e);
          }
        });
        formsReadyForListeners.value = true;
      });
    };
    
    // Fetch content when the component is mounted
    onMounted(async () => {
      startTimer('bodyContent');
      startTimer('bodyContent');
      window.scrollTo(0, 0);
      RemoveExternalScripts();
      loadExternalCSS(); // ✅ load CSS first
      await fetchContent(route.fullPath); // then fetch HTML
    });
    // Add form listeners whenever the component updates
    onUpdated(async () => {
      window.scrollTo(0, 0);
      if (LoadExternalScripts.value) {
        //trigger the loading of the scripts and defs
        await addJsDefinitions();
        await addScriptCode();
        loadExternalScripts();
        LoadExternalScripts.value = false;
      }
      
      if (!formsReadyForListeners.value) {
        addListenerToForms();
        addListenerToLinks();
        SendEventWhenContentLoaded();
      }
    });
    
    // Re-fetch content whenever the route.fullPath changes
    watch(
    () => route.fullPath,
    () => {
      startTimer('bodyContent');
      if (IsRedirect.value) {
        IsRedirect.value = false;
      } else {
        fetchContent(stripLangPrefix(route.fullPath));
      }
    }
    );
    
    function FormHandler(form: HTMLFormElement, e: Event) {
      loading.value = true;
      
      var formMethod = form.getAttribute('method');
      
      e.preventDefault();
      e.target?.removeEventListener('submit', (event) => FormHandler(form, event));
      
      // Get form data
      var formData = new FormData(form);
      // if the form has a submit input with a value add that to the form data
      var submitInput = form.querySelector('input[type="submit"]') as HTMLInputElement;
      if (submitInput) {
        if (submitInput.name && submitInput.value) formData.append(submitInput.name, submitInput.value);
      }
      
      var formAction = form.getAttribute('action');
      // Performing POST request on form submit
      
      if (formMethod?.toLowerCase() === 'post') {
        axiosClassic
        .post(`${formAction}`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data', 'LEVELTECH-REFERER': route.fullPath },
        })
        .then((response) => {
          // Get X-Guzzle-Redirect-History header and set router to that URL
          var redirectUrl = response.headers['x-guzzle-redirect-history'];
          
          if (redirectUrl) {
            // Split the string by comma and get the last URL
            RedirectToUrl(redirectUrl);
            InsertResponseIntoDOM(response);
          } else {
            // Handling response
            InsertResponseIntoDOM(response);
            if (formAction) {
              IsRedirect.value = true;
              router.replace(stripLangPrefix(formAction));
            }
          }
        })
        .catch((response) => {
          // Handling errors
          errorMessage.value = `Error submitting the form: ${response}`;
          loading.value = false;
          if (!loading.value) stopTimer('bodyContent');
          SendEventWhenContentLoaded();
        })
        .finally(() => {
          loading.value = false;
          if (!loading.value) stopTimer('bodyContent');
          SendEventWhenContentLoaded();
        });
      } else {
        // Get form action and remove the base url and query string
        if (formAction) {
          // Add form data to end of formaction as query string as the get form request usually does
          formAction = formAction + '?' + new URLSearchParams(formData as any).toString() + '&guid=' + new Date().getTime();
          // Redirect to the form action
          router.push(stripLangPrefix(formAction));
          
        } else {
          formAction = new URL(form.action).pathname;
          // Add form data to end of formaction as query string as the get form request usually does
          formAction = formAction + '?' + new URLSearchParams(formData as any).toString();
          // Redirect to the form action
          router.push(stripLangPrefix(formAction));
        }
      }
    }
    
    function RedirectToUrl(redirectUrl: any) {
      // Split the string by comma and get the last URL
      redirectUrl = redirectUrl.split(',').pop().trim();
      // Remove base url so only first part after / is left
      redirectUrl = new URL(redirectUrl).pathname + new URL(redirectUrl).search;
      
      IsRedirect.value = true;
      
      router.push(stripLangPrefix(redirectUrl));
    }
    
    function UpdatePathWithUrl(redirectUrl: any) {
      // Split the string by comma and get the last URL
      redirectUrl = redirectUrl.split(',').pop().trim();
      // Remove base url so only first part after / is left
      redirectUrl = new URL(redirectUrl).pathname + new URL(redirectUrl).search;      
      router.replace(stripLangPrefix(redirectUrl));
    }
    
    function RemoveExternalScripts() {
      const existingScripts = document.querySelectorAll('script.defered_script');
      existingScripts.forEach((script) => {
        document.body.removeChild(script);
      });
      if (typeof $ !== 'undefined') {
        $(document).off();
      }
    }
    
    async function loadExternalScripts() {
      // Load the external scripts by sending an event
      const event = new Event('reload_classic_javascript');
      document.dispatchEvent(event);
    }
    
    // Function to load the external CSS file based on the sport title
    // Maintain a single managed <link id="sport-css"> and just update its href
    function loadExternalCSS() {
      console.groupCollapsed("loadExternalCSS()");
      const baseHref = `/css/crush_specific_${sportTitle.value}.css`;
      const newHref = `${getResourceUrl(baseHref)}?v=${new Date().toISOString().slice(0, 13)}`;
      
      let link = document.getElementById('sport-css') as HTMLLinkElement | null;

      // Create a dedicated placeholder for the sport-specific CSS
      if (!link) {
        console.log("Creating new <link id='sport-css'>");
        link = document.createElement('link');
        link.id = 'sport-css';
        link.rel = 'stylesheet';
        link.type = 'text/css';
        document.head.appendChild(link);
      } else {
        console.log("Reusing existing <link id='sport-css'>");
      }
      
      link.onload = () => {
        console.log(`CSS loaded: ${newHref}`);
        console.groupEnd();
      };
      
      // Update only the href (browser will handle caching)
      console.log(`Updating href → ${newHref}`);
      link.href = newHref;
      console.groupEnd();
    }
    
    // Function to handle the response from the classic API
    async function InsertResponseIntoDOM(response: AxiosResponse) {
      formsReadyForListeners.value = false;
      const html = await response.data;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Choose what part of the HTML to inject into your app
      // Import Head Section Only | externalContent.value = doc.head.innerHTML;
      // Import Entire Classic Site | externalContent.value = doc.documentElement.outerHTML;
      // Import Body Section Only |
      externalContent.value = doc.body.outerHTML;
      
      errorMessage.value = '';
      // Trigger toast for success
      // showToast('success', 'Content loaded successfully');
      
      await GetJavascriptValues(response);
    }
    
    async function GetJavascriptValues(response: AxiosResponse<any, any>) {
      JsDefs.value = await ReturnJSDefs(response);
      if (JsDefs.value) {
        LoadJsDefs.value = JsDefs.value;
      }
      JsCodeFromAxiosResponse.value = await ReturnJSCode(response);
      if (JsCodeFromAxiosResponse.value) {
        LoadScript.value = JsCodeFromAxiosResponse.value;
      }
      // Pass scriptUrl into DynamicScript component
      LoadExternalScripts.value = true;
    }
    
    const addListenerToLinks = () => {
      const externalContent = document.getElementById('external-content');
      externalContent?.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', linkHandler);
      });
    };
    
    const linkHandler = function (e: Event) {
      e.preventDefault();
      e.currentTarget?.removeEventListener('click', linkHandler);
      const href = (e.currentTarget as HTMLAnchorElement)?.getAttribute('href');
      if (href) {
        if (href.startsWith('http')) {
          window.open(href, '_blank');
        } else if (href.startsWith('#')) {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        } else if (href.startsWith('mailto:')) {
          // Do mailto
          window.location.href = href;
        } else if (href.startsWith('/')) {
          router.push(href);
        } else {
          router.push(href);
        }
      }
    };
    
    async function addScriptCode() {
      let existingScript = document.getElementById('dynamicScript');
      if (existingScript) {
        existingScript.remove();
      }
      // Create new script
      let dynamicJavaScript = document.createElement('script');
      dynamicJavaScript.type = 'text/javascript';
      dynamicJavaScript.id = 'dynamicScript';
      dynamicJavaScript.textContent = JsCodeFromAxiosResponse.value ? JsCodeFromAxiosResponse.value : '';
      document.head.appendChild(dynamicJavaScript);
    }
    
    async function addJsDefinitions() {
      let existingScript = document.getElementById('JsDefs');
      if (existingScript) {
        existingScript.remove();
      }
      
      let dynamicJavaScript = document.createElement('script');
      dynamicJavaScript.type = 'text/javascript';
      dynamicJavaScript.id = 'JsDefs';
      dynamicJavaScript.textContent = JsDefs.value ? JsDefs.value : '';
      document.head.appendChild(dynamicJavaScript);
    }
    
    function SendEventWhenContentLoaded() {
      const event = new Event('body_content_loaded');
      document.dispatchEvent(event);
    }
    
    // Add listener to document tollisten for ajaxComplete event
    document.addEventListener('dashboard_block_loaded', function () {
      addListenerToLinks();
      addListenerToForms();
    });
    
    // Returning reactive variables to be used in the template
    return {
      externalContent,
      errorMessage,
      loading,
      overlay,
      JsCodeFromAxiosResponse,
      JsDefs,
      LoadScript,
      LoadJsDefs,
    };
  },
  components: { Error404, LevelsLoading },
});
</script>

<style lang="scss">
// import the Sass Variables File

// Slow down the speed of the loader animation
.fetch_content_skeleton_loader .v-progress-circular--indeterminate .v-progress-circular__overlay {
  animation: progress-circular-dash 5.4s ease-in-out infinite, progress-circular-rotate 3s linear infinite;
}

// Center the spinner and loading text
.fetch_content_spinner_container {
  height: calc(100vh - 64px - 130px);
  display: flex;
  flex-direction: column;
  align-content: center;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

// Increase spinner and loader text Height on Mobile
@media (max-width: $mobile-screen) {
  .fetch_content_spinner_container {
    height: calc(100vh - 64px - 46px);
  }
}

.fetch_content_spinner_text {
  font-family: $mongoose-font;
  font-size: 22px;
  font-weight: 400;
}
</style>
